import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tag, Plus, Trash2, Edit, Loader2, Search, Calendar } from 'lucide-react';
import { toast } from 'sonner';

export default function Vouchers() {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    code: '', name: '', discount_type: 'percentage', discount_value: 0,
    min_purchase: 0, max_discount: 0, usage_limit: 0, per_customer_limit: 0,
    start_date: '', end_date: '', is_active: true
  });

  const fetchData = async () => {
    try {
      const res = await api.get('/tenant/vouchers');
      setVouchers(res.data.vouchers || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const resetForm = () => {
    setForm({ code: '', name: '', discount_type: 'percentage', discount_value: 0, min_purchase: 0, max_discount: 0, usage_limit: 0, per_customer_limit: 0, start_date: '', end_date: '', is_active: true });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) { toast.error('Kode voucher wajib diisi'); return; }
    if (form.discount_value <= 0) { toast.error('Nilai diskon harus > 0'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/tenant/vouchers/${editingId}`, form);
        toast.success('Voucher berhasil diupdate');
      } else {
        await api.post('/tenant/vouchers', form);
        toast.success('Voucher berhasil dibuat');
      }
      resetForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan voucher');
    } finally { setSaving(false); }
  };

  const handleEdit = (v) => {
    setForm({
      code: v.code, name: v.name, discount_type: v.discount_type, discount_value: v.discount_value,
      min_purchase: v.min_purchase || 0, max_discount: v.max_discount || 0,
      usage_limit: v.usage_limit || 0, per_customer_limit: v.per_customer_limit || 0,
      start_date: v.start_date || '', end_date: v.end_date || '', is_active: v.is_active
    });
    setEditingId(v.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus voucher ini?')) return;
    try {
      await api.delete(`/tenant/vouchers/${id}`);
      toast.success('Voucher dihapus');
      fetchData();
    } catch (e) { toast.error('Gagal menghapus'); }
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin h-8 w-8 text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="vouchers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Voucher & Promo</h1>
          <p className="text-sm text-slate-500">Kelola voucher diskon untuk produk Anda</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="create-voucher-btn">
          <Plus size={16} className="mr-2" /> Buat Voucher
        </Button>
      </div>

      {showForm && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">{editingId ? 'Edit Voucher' : 'Buat Voucher Baru'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Kode Voucher *</Label>
                  <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="DISKON10" className="uppercase" data-testid="voucher-code-input" />
                </div>
                <div>
                  <Label>Nama Voucher</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Diskon 10%" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Tipe Diskon</Label>
                  <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Persentase (%)</SelectItem>
                      <SelectItem value="fixed">Nominal (Rp)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nilai Diskon *</Label>
                  <Input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: parseInt(e.target.value) || 0 })} data-testid="discount-value-input" />
                </div>
                <div>
                  <Label>Maks. Diskon (Rp)</Label>
                  <Input type="number" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: parseInt(e.target.value) || 0 })} placeholder="0 = unlimited" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Min. Pembelian (Rp)</Label>
                  <Input type="number" value={form.min_purchase} onChange={(e) => setForm({ ...form, min_purchase: parseInt(e.target.value) || 0 })} />
                </div>
                <div>
                  <Label>Batas Penggunaan</Label>
                  <Input type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: parseInt(e.target.value) || 0 })} placeholder="0 = unlimited" />
                </div>
                <div>
                  <Label>Batas Per Customer</Label>
                  <Input type="number" value={form.per_customer_limit} onChange={(e) => setForm({ ...form, per_customer_limit: parseInt(e.target.value) || 0 })} placeholder="0 = unlimited" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tanggal Mulai</Label>
                  <Input type="datetime-local" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                </div>
                <div>
                  <Label>Tanggal Berakhir</Label>
                  <Input type="datetime-local" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label>Aktif</Label>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={saving} data-testid="save-voucher-btn">
                  {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                  {editingId ? 'Update' : 'Simpan'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>Batal</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {vouchers.length === 0 ? (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="py-12 text-center">
              <Tag size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Belum ada voucher</p>
            </CardContent>
          </Card>
        ) : (
          vouchers.map(v => (
            <Card key={v.id} className="border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                      <Tag size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-slate-900">{v.code}</span>
                        <Badge variant={v.is_active ? 'default' : 'secondary'}>
                          {v.is_active ? 'Aktif' : 'Nonaktif'}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500">
                        {v.discount_type === 'percentage' ? `${v.discount_value}%` : `Rp ${fmt(v.discount_value)}`}
                        {v.min_purchase > 0 && ` · Min. Rp ${fmt(v.min_purchase)}`}
                        {v.max_discount > 0 && ` · Maks. Rp ${fmt(v.max_discount)}`}
                      </p>
                      <p className="text-xs text-slate-400">
                        Digunakan: {v.usage_count || 0}{v.usage_limit > 0 ? `/${v.usage_limit}` : ''} kali
                        {v.end_date && ` · Berakhir: ${new Date(v.end_date).toLocaleDateString('id-ID')}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(v)}>
                      <Edit size={14} className="mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDelete(v.id)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
